import type { SenderAction, SenderStatus } from "@/api/custom_types";
import { useStatusMap } from "@/hooks";
import { useTranslation } from "@/hooks/useTranslation";
import {
	useDeleteMultipleSenders,
	useReloginForOneSender,
	useStopResumeMultipleSenders,
} from "@/services";
import {
	ActionItem,
	ActionMenu,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	toast,
} from "@st-marketplace/ui";
import { type FC, type ReactNode, useState } from "react";
import SendersEditDialog from "./SendersEditDialog";
import type { SenderRow } from "./types";

export interface SendersSingleActionProps {
	selectedSender: Pick<SenderRow, "id" | "status"> & { status: SenderStatus };
}

interface SenderActionDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	description: ReactNode;
	confirmLabel: string;
	loading: boolean;
	onConfirm: () => void;
}

const SenderActionDialog: FC<SenderActionDialogProps> = ({
	open,
	onOpenChange,
	description,
	confirmLabel,
	loading,
	onConfirm,
}) => {
	const { t } = useTranslation();

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{t("action.areYouSure")}</DialogTitle>
				</DialogHeader>
				<div className="px-4">{description}</div>
				<DialogFooter
					buttons={{
						secondary: {
							label: t("common.button.no"),
							props: {
								type: "button",
								onClick: () => onOpenChange(false),
							},
						},
						primary: {
							label: confirmLabel,
							props: {
								type: "button",
								loading,
								onClick: onConfirm,
							},
						},
					}}
				/>
			</DialogContent>
		</Dialog>
	);
};

const SendersSingleAction: React.FC<SendersSingleActionProps> = ({
	selectedSender,
}) => {
	const { t } = useTranslation();

	const deleteMultipleSenders = useDeleteMultipleSenders();
	const stopResumeMultipleSenders = useStopResumeMultipleSenders();
	const reloginForOneSender = useReloginForOneSender();

	const { senderStatusMap } = useStatusMap();

	const [menuActionOpen, setMenuActionOpen] = useState<boolean>(false);
	const [playDialog, setPlayDialog] = useState<boolean>(false);
	const [pauseDialog, setPauseDialog] = useState<boolean>(false);
	const [editDialog, setEditDialog] = useState<boolean>(false);
	const [deleteDialog, setDeleteDialog] = useState<boolean>(false);
	const [reloginDialog, setReloginDialog] = useState<boolean>(false);

	const HandleDeleteMultipleSenders = () => {
		deleteMultipleSenders.mutate(
			{ body: [selectedSender.id] },
			{
				onSuccess: () => {
					setDeleteDialog(false);
					setMenuActionOpen(false);
				},
			},
		);
	};

	const handleStopResumeMultipleSenders = (action: SenderAction) => {
		stopResumeMultipleSenders.mutate(
			{ body: { action, sender_ids: [selectedSender.id] } },
			{
				onSuccess: () => {
					if (action === "PAUSE")
						toast.success(t("sender.action.successPauseSenders"));
					else toast.success(t("sender.action.successResumeSenders"));
					setPauseDialog(false);
					setPlayDialog(false);
					setMenuActionOpen(false);
				},
			},
		);
	};

	const handleReloginSender = () => {
		reloginForOneSender.mutate(
			{ path: { sender_id: selectedSender.id } },
			{
				onSuccess: () => {
					toast.success(t("sender.action.successReloginSenders", { value: 1 }));
					setReloginDialog(false);
					setMenuActionOpen(false);
				},
			},
		);
	};

	const statusCaps = senderStatusMap?.[selectedSender.status];

	const canResume = Boolean(statusCaps?.can_resume);
	const canPause = Boolean(statusCaps?.can_pause);
	const canRelogin = Boolean(statusCaps?.can_relogin);

	return (
		<>
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation-only container; inner menu remains keyboard-accessible */}
			<div onClick={(event) => event.stopPropagation()}>
				<ActionMenu open={menuActionOpen} onOpenChange={setMenuActionOpen}>
					<ActionItem
						leadingIconName="loop-left-fill"
						loading={reloginForOneSender.isPending}
						disabled={!canRelogin}
						onClick={() => {
							setMenuActionOpen(false);
							setReloginDialog(true);
						}}
					>
						{t("action.relogin")}
					</ActionItem>
					<ActionItem
						leadingIconName="pause-circle-line"
						loading={stopResumeMultipleSenders.isPending}
						disabled={!canPause}
						onClick={() => {
							setMenuActionOpen(false);
							setPauseDialog(true);
						}}
					>
						{t("action.pause")}
					</ActionItem>
					<ActionItem
						leadingIconName="play-circle-line"
						loading={stopResumeMultipleSenders.isPending}
						disabled={!canResume}
						onClick={() => {
							setMenuActionOpen(false);
							setPlayDialog(true);
						}}
					>
						{t("action.play")}
					</ActionItem>
					<ActionItem
						leadingIconName="edit-line"
						onClick={() => {
							setMenuActionOpen(false);
							setEditDialog(true);
						}}
					>
						{t("action.edit")}
					</ActionItem>
					<ActionItem
						leadingIconName="delete-bin-line"
						loading={deleteMultipleSenders.isPending}
						onClick={() => {
							setMenuActionOpen(false);
							setDeleteDialog(true);
						}}
					>
						{t("action.delete")}
					</ActionItem>
				</ActionMenu>
			</div>

			<SenderActionDialog
				open={playDialog}
				onOpenChange={setPlayDialog}
				description={t("sender.action.playMessage", { value: 1 })}
				confirmLabel={t("action.button.play")}
				loading={stopResumeMultipleSenders.isPending}
				onConfirm={() => handleStopResumeMultipleSenders("RESUME")}
			/>

			<SenderActionDialog
				open={pauseDialog}
				onOpenChange={setPauseDialog}
				description={t("sender.action.pauseMessage", { value: 1 })}
				confirmLabel={t("action.button.pause")}
				loading={stopResumeMultipleSenders.isPending}
				onConfirm={() => handleStopResumeMultipleSenders("PAUSE")}
			/>

			{editDialog && (
				<SendersEditDialog
					senderId={selectedSender.id}
					open={true}
					onClose={() => setEditDialog(false)}
				/>
			)}

			<SenderActionDialog
				open={deleteDialog}
				onOpenChange={setDeleteDialog}
				description={t("sender.action.deleteMessage", { value: 1 })}
				confirmLabel={t("action.button.delete")}
				loading={deleteMultipleSenders.isPending}
				onConfirm={HandleDeleteMultipleSenders}
			/>
			<SenderActionDialog
				open={reloginDialog}
				onOpenChange={setReloginDialog}
				description={t("sender.action.reloginMessage", { value: 1 })}
				confirmLabel={t("action.button.confirmRelogin")}
				loading={reloginForOneSender.isPending}
				onConfirm={handleReloginSender}
			/>
		</>
	);
};

export default SendersSingleAction;
